/*
  bc-tile-lazy.js

  Loads the cue bands of tiles that sit outside a horizontal rail's visible
  window, one tile ahead of the scroll.

  Why this exists, when the images already carry loading="lazy".

  Native lazy loading measures distance from the VIEWPORT, and its threshold
  is generous - a few hundred to a couple of thousand pixels depending on the
  connection. The McDermott section is three rows of 264px tiles and a rail
  535px wider than its own track, so every tile in it, including both rows
  below the fold and the tiles scrolled off the rail, falls inside that
  threshold. Measured on the draft at 1280x720, scrolled to the top, rail
  never scrolled: 72 files, 896KB, for a rail showing four tiles. Native lazy
  was not being defeated by the horizontal container - it was being satisfied
  by it.

  So the tiles that can only be reached by scrolling the rail - index 5 and
  up within their row - ship with data-src instead of src, and this file
  turns data-src into src when the tile comes within one tile-width of the
  rail's window. The first five ship their bands in the HTML on every
  screen: deferring any of them and promoting from script was tried, and a
  promoted tile cannot start loading until the script has run, which showed
  as seconds of empty plank on the tiles nearest the fold. IntersectionObserver already accounts for clipping by
  scrolling ancestors, and pointing root AT the rail is what buys the
  one-tile lookahead: rootMargin expands the root's own box, and with the
  default root that box is the viewport, which the rail is nowhere near the
  edge of.

  It runs after bc-desktop-rail.js so the track element exists to be used as
  a root, and again when the desktop breakpoint is crossed, because crossing
  it is what creates or destroys that track.
*/
(function () {
  'use strict';

  var SELECTOR = '.mcd-tile img[data-src], .longoni-tile img[data-src]';
  var BANDS = '.mcd-tile__cue-row img';
  var FALLBACK_TILE = 260;   // px, if a tile has no box to measure yet
  var REVEAL_CEILING = 800;  // ms, see gate()
  var observers = [];
  // Every gated band's reveal, so the document-level backstop below can
  // clear anything still hidden however it got there.
  var pending = [];

  function revealAll() {
    var queued = pending;
    pending = [];
    queued.forEach(function (reveal) { try { reveal(); } catch (error) {} });
  }

  /*
    THE DECODE GATE

    A band is a wide, short JPEG, and a JPEG paints as it arrives: the
    reported symptom was the right-hand end of every first-row band
    missing, a seam across the placeholder, until the rest of the file
    landed. Nothing about priority fixes that - arriving sooner still
    means arriving in pieces.

    So a band that is not ready is transparent, the row's own colour
    shows through, and it is revealed only once decode() has resolved -
    which is the browser saying the whole frame can be painted at once.

    THE CLASS IS ADDED BY SCRIPT, NEVER BY THE MARKUP. Without JS there is
    no pending class, so the bands are plain visible images that may paint
    progressively - which is the honest fallback and the same shape as the
    noscript twins. A stylesheet that hides content and waits for a script
    to reveal it is the trap this file must not repeat.

    An image that is ALREADY complete is left alone: it cannot paint in
    pieces, and hiding it to fade it back in would be a flash where there
    was none.

    REVEAL_CEILING is the safety net, and it is deliberately short.
    decode() can hang - it does in a hidden tab - and a band must never be
    permanently invisible because a promise did not settle. 800ms is long
    enough for a decode on a warm cache and short enough that a slow one
    ends up showing a progressive band rather than a long empty plank.
    Three seconds was the first try and it was the wrong trade: it turned
    a flicker into seconds of blank.
  */
  function gate(img) {
    if (img.getAttribute('data-bc-band-gated')) return;
    img.setAttribute('data-bc-band-gated', '1');

    /*
      An <img> with no src reports complete === true. That is the whole
      trap: a deferred band is gated BEFORE its src is set, so reading
      complete on its own took the already-loaded branch, called decode()
      on nothing, had it reject, and revealed the band a millisecond
      before the src that would paint it progressively. Measured: three
      bands gated at t and revealed at t+1 with naturalWidth 0.

      So "ready" means it has a source AND that source is in.
    */
    var loaded = function () {
      return !!(img.getAttribute('src') || img.currentSrc) && img.complete && img.naturalWidth > 0;
    };
    if (loaded()) return;

    /*
      The placeholder belongs to the PENDING state, not to the row.

      It was a plain background-color on .mcd-tile__cue-row, and that
      painted something no loading state ever explains: the row is 28px
      and a band is far shorter in it - 19px for the top composite, 15px
      for the butt, 10px for the shaft, whose asset is 1500x61 - so the
      colour showed as a permanent plank in the leftover, worst on the
      third row. Reported as the shaft never being revealed; it was
      revealed, and sitting on 18px of my own backdrop.

      So the row wears the colour only while its band is pending, and
      takes it off with the same reveal.
    */
    var row = img.closest ? img.closest('.mcd-tile__cue-row') : null;

    img.classList.add('bc-band--pending');
    if (row) row.classList.add('is-band-pending');

    var revealed = false;
    var reveal = function () {
      if (revealed) return;
      revealed = true;
      img.classList.remove('bc-band--pending');
      if (row) row.classList.remove('is-band-pending');
    };
    var afterLoad = function () {
      // finally-shaped: the reveal happens down every path, decoded or
      // not, and reveal itself is once-only so a double call is free.
      if (img.decode) { img.decode().then(reveal, reveal); return; }
      reveal();
    };

    // load fires whenever the src arrives, now or when loadNow sets it.
    img.addEventListener('load', afterLoad, { once: true });
    img.addEventListener('error', reveal, { once: true });
    // And immediately if it is already in - a shared src that another
    // tile has already fetched is complete before this listener exists.
    if (loaded()) afterLoad();
    window.setTimeout(reveal, REVEAL_CEILING);
    pending.push(reveal);
  }

  /*
    At init, gate only a band that HAS a source and has not finished it.

    Two bands are deliberately left alone. One already complete cannot
    paint in pieces, so hiding it to fade it back would be a flash where
    there was none. One with no src yet is not waiting on anything - it is
    a tile 6+ whose src arrives later, and loadNow gates it then, so its
    ceiling starts when the download does rather than expiring while it
    still has nothing to show.
  */
  function gateAll(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(BANDS), function (img) {
      if (!img.getAttribute('src') && !img.currentSrc) return;
      if (img.complete && img.naturalWidth > 0) return;
      gate(img);
    });
  }

  function loadNow(img) {
    var src = img.getAttribute('data-src');
    if (!src) return;
    // Gate BEFORE the src lands, so a deferred band is never briefly a
    // half-drawn one either.
    gate(img);
    /*
      AND TAKE loading="lazy" OFF, or the src lands on an image that
      refuses to fetch it.

      The deferred markup carries loading="lazy" as a belt-and-braces
      fallback for a page whose scripts never run. But this function sets
      src one tile-width AHEAD of the rail's window, deliberately - and a
      lazy image that is still outside the viewport when its src arrives
      does not start the request. Chrome then leaves it at complete
      false, naturalWidth 0, with no error and no timeout: measured on
      the carom rail 2026-09-11, six tiles whose butt rows never painted
      even after they had been scrolled into view for twenty seconds,
      while fetch() of the same URLs returned 200 and the full bytes. The
      shared shaft bands hid it - they were already in cache from the
      first five tiles, so only the per-model rows showed as empty.

      This script IS the lazy mechanism for these bands; the attribute
      has no work left to do once it has fired.
    */
    img.setAttribute('loading', 'eager');
    img.removeAttribute('data-src');
    img.setAttribute('src', src);
  }

  function loadTile(tile) {
    Array.prototype.forEach.call(tile.querySelectorAll('img[data-src]'), loadNow);
  }

  // The nearest ancestor that actually scrolls sideways: the rail's track on
  // desktop, the row itself on phones, and nothing at all if the row is a
  // plain grid - in which case the viewport is the honest root.
  function scrollRootOf(el) {
    for (var node = el.parentElement; node && node !== document.body; node = node.parentElement) {
      var ox = window.getComputedStyle(node).overflowX;
      if (ox === 'auto' || ox === 'scroll') return node;
    }
    return null;
  }

  function tilesPending() {
    var imgs = document.querySelectorAll(SELECTOR);
    var tiles = [];
    Array.prototype.forEach.call(imgs, function (img) {
      var tile = img.closest ? img.closest('.mcd-tile, .longoni-tile') : null;
      if (tile && tiles.indexOf(tile) === -1) tiles.push(tile);
    });
    return tiles;
  }

  function wire() {
    observers.forEach(function (o) { o.disconnect(); });
    observers = [];

    // Every band on the page, eager or deferred, goes behind the gate.
    gateAll(document);

    var tiles = tilesPending();
    if (!tiles.length) return;

    if (!('IntersectionObserver' in window)) {
      // No observer: show the cue rather than an empty card.
      tiles.forEach(loadTile);
      return;
    }

    // One observer per distinct root, so a page with several rails does not
    // get one observer per tile.
    var roots = [], groups = [];
    tiles.forEach(function (tile) {
      var root = scrollRootOf(tile);
      var i = roots.indexOf(root);
      if (i === -1) { roots.push(root); groups.push([tile]); }
      else groups[i].push(tile);
    });

    roots.forEach(function (root, i) {
      var group = groups[i];
      var width = Math.round(group[0].getBoundingClientRect().width) || FALLBACK_TILE;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          loadTile(entry.target);
          io.unobserve(entry.target);
        });
      }, { root: root, rootMargin: '0px ' + width + 'px' });
      group.forEach(function (tile) { io.observe(tile); });
      observers.push(io);
    });
  }

  function start() {
    wire();
    var mq = window.matchMedia('(min-width: 750px)');
    var again = function () { wire(); };
    if (mq.addEventListener) mq.addEventListener('change', again);
    else if (mq.addListener) mq.addListener(again);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', wire);

  /*
    The backstop. Whatever happened - a decode that never settled, a load
    event missed on a shared src, a tile restored from the back/forward
    cache with its classes intact - no band is left invisible once the
    document itself is done. The per-image 3s ceiling still runs; this is
    the one that cannot be missed because it does not depend on the image
    at all.
  */
  window.addEventListener('load', revealAll);
  window.addEventListener('pageshow', revealAll);
})();
